import { MoreOutlined } from "@ant-design/icons";
import {
  Button,
  Dropdown,
  MenuProps,
  Space,
  TablePaginationConfig,
} from "antd";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { Column, RecordCountBadge, Table } from "components";
import {
  StyledTableHeader,
  StyledTableWrapper,
} from "components/table/table.styled";
import { usePermissions, useUserContext } from "features/users";
import { CreateApprovedDTO } from "features/withdraw/api/withdraw-api.service";
import { ApproveModal } from "features/withdraw/components/modal/ConfirmModal";
import { RejectModal } from "features/withdraw/components/modal/RejectModal";
import { UsersWithWithdrawFilterFormPaginated } from "features/withdraw/components/users-with-wallet-filter-form/type";
import { useSuperAdminManual } from "features/withdraw/use-cases/super-admin-manual";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { getRoute } from "routes/utils";
import { UserWithithdraw } from "types/withdraw";
import { DEFAULT_TIMEZONE, formatDate } from "utils/date";
// ../users-with-wallet-filter-form/type
// const { useBreakpoint } = Grid;
// ../modal/ConfirmModal

type UsersWithWalletTableProps = {
  isLoading: boolean;
  data?: any;
  tableParams: UsersWithWithdrawFilterFormPaginated;
  updateParams: Dispatch<SetStateAction<UsersWithWithdrawFilterFormPaginated>>;
};

export const UsersWithWithdrawTable = ({
  isLoading,
  data,
  tableParams,
  updateParams,
}: UsersWithWalletTableProps) => {
  const { t } = useTranslation();
  // const screens = useBreakpoint();
  const navigate = useNavigate();
  const { user }: any = useUserContext();
  const permissions = usePermissions();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const { mutate: handleBulkAction, isLoading: isBulkLoading } =
    useSuperAdminManual();

  if (!permissions) return null;

  const isDisabled = selectedRowKeys.length === 0 && !isAllSelected;

  const handleBulkActionClick = (
    actionType: number,
    remark?: string,
    referenceNumber?: string
  ) => {
    const selectableIds =
      data?.items
        ?.filter((item: any) => item.status_id === 2)
        .map((item: any) => item.id) || [];

    const isAll = selectedRowKeys.length === selectableIds.length;

    const selectedRecords = data?.items?.filter((item: any) =>
      selectedRowKeys.includes(item.id)
    );

    const payload: CreateApprovedDTO = {
      WithdrawRequestIds: isAll
        ? selectedRecords.map((item: any) => item.id) // ✅ IMPORTANT: backend expects empty when all
        : selectedRecords.map((item: any) => item.id),

      CreatedBy: user?.id,
      LoginUserName: user?.user_name,
      Remark: remark || "",
      ActionType: actionType,
      IsAll: isAll,
      RoleType: user?.role,

      ...(actionType === 3 && {
        RefrenceNumber: referenceNumber,
      }),
    };

    handleBulkAction(payload, {
      onSuccess: () => {
        setSelectedRowKeys([]);
      },
    });
  };
  const rowSelection = {
    selectedRowKeys,

    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);

      setIsAllSelected(false);
    },

    onSelectAll: (selected: boolean) => {
      setIsAllSelected(selected);

      if (selected) {
        const allIds = data?.items
          ?.filter((item: any) => item.status_id === 2)
          .map((item: any) => item.id);

        setSelectedRowKeys(allIds || []);
      } else {
        setSelectedRowKeys([]);
      }
    },

    getCheckboxProps: (record: any) => ({
      disabled: record.status_id !== 1,
    }),
  };
  //
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserWithithdraw> | SorterResult<UserWithithdraw>[]
  ) => {
    const sortBy = () => {
      if (Array.isArray(sorter)) return;

      const sortOrder =
        sorter.order === "ascend" ? `${sorter.field}` : `-${sorter.field}`;

      if (sorter?.field === "last_transaction_date") {
        return {
          order_by_balance: undefined,
          order_by_transaction: sortOrder,
        };
      }
    };

    updateParams({
      ...tableParams,
      page: pagination.current,
      size: pagination.pageSize,
      ...sortBy(),
    });
  };
  const handleValue = (matchId: any) => {
    navigate(getRoute(ROUTES.WITHDRAW_detail, matchId));
  };
  const handleValueRejectORManual = (matchId: number, key: any) => {
    if (key == 1) {
      navigate(getRoute(ROUTES.WITHDRAW_REJECT, matchId));
    } else {
      navigate(getRoute(ROUTES.WITHDRAW_MANUAL, matchId));
    }
  };
  // const handleValueReject = (matchId: any) => {
  //   navigate(getRoute(ROUTES.WITHDRAW_REJECT, matchId));

  // };
  const items = (record: any): MenuProps["items"] => {
    return [
      {
        label: "View",
        onClick: () =>
          navigate(getRoute(ROUTES.WITHDRAW_detail_data, record.id)),
        key: "2",
      },
    ].filter(Boolean);
  };

  // const tableWidth = screens.xxl ? { x: 1440 } : { x: 1100 };

  return (
    <StyledTableWrapper>
      <StyledTableHeader>
        <RecordCountBadge
          text={t("wallet.foundUsers")}
          count={data?.total || 0}
        />
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            lineHeight: "1.5",
          }}
        >
          {user?.role === "Li3ibAccountant" && selectedRowKeys.length > 0 && (
            <>
              <Button
                type="primary"
                style={{ backgroundColor: "#53a869" }}
                onClick={() => setOpenApproveModal(true)}
                disabled={isDisabled}
              >
                Bulk Approve
              </Button>

              <Button
                type="primary"
                danger
                style={{
                  borderColor: "#ff4d4f",
                  color: "#ff4d4f",
                  backgroundColor: "transparent",
                }}
                onClick={() => setOpenRejectModal(true)}
              >
                Bulk Reject
              </Button>

              <div className="total-selected-badge">
                <span style={{ fontWeight: "500" }}>
                  Total Selected:{selectedRowKeys.length}{" "}
                </span>
              </div>
            </>
          )}
        </div>
      </StyledTableHeader>
      <Table<UserWithithdraw>
        rowSelection={rowSelection}
        loading={isLoading || isBulkLoading}
        dataSource={data?.items}
        className="aprv_btn"
        rowKey="id"
        onChange={handleTableChange}
        pagination={{
          current: tableParams.page,
          defaultPageSize: tableParams.size,
          total: data?.total,
        }}
        scroll={{ x: 1440 }}
        // onRow={(record: any) => {
        //   return {
        //     className: 'clickable-row',
        //     onClick: () => {
        //       if (record.status_id !== 1) {
        //       navigate(getRoute(ROUTES.WITHDRAW_detail_data, record.id));
        //       }
        //     },
        //   };
        // }}
      >
        <Column
          title={t("wallet.table.userName")}
          dataIndex={"user_name"}
          render={(_, record: any) => record.user_name}
          width="200px"
        />
        <Column<UserWithithdraw>
          title={t("wallet.table.phone")}
          dataIndex={"phone_number"}
          // width={screens.xxl ? 'auto' : 170}
        />
        <Column<UserWithithdraw>
          title={t("withdraw.table.amount")}
          dataIndex={"amount"}
          // sorter={true}
          // sortDirections={['descend', 'ascend', 'descend']} // workaround to remove null as an option
          // width={screens.xxl ? 'auto' : 130}
          render={(_, record: any) => (
            <span>{`${record.amount ? `${record.amount} KD` : ""}`}</span>
          )}
        />
        <Column<UserWithithdraw>
          title={t("withdraw.table.Date")}
          dataIndex={"request_date"}
          width={150}
          render={(_, record: any) => (
            <div style={{ whiteSpace: "nowrap" }}>
              {formatDate({
                date: record.request_date,
                format: "date",
                timeZone: DEFAULT_TIMEZONE,
              } as any)}
              <Space style={{ padding: "2px" }}>
                {formatDate({
                  date: record.request_date,
                  format: "time",
                  timeZone: DEFAULT_TIMEZONE,
                } as any)}
              </Space>
            </div>
          )}
          // width={screens.xxl ? 'auto' : 130}
        />

        <Column<UserWithithdraw>
          title={t("withdraw.table.transactionDate")}
          dataIndex={"transaction_date"}
          width={150}
          render={(_, record: any) => (
            <div style={{ whiteSpace: "nowrap" }}>
              {formatDate({
                date: record.transaction_date,
                format: "date",
                timeZone: DEFAULT_TIMEZONE,
              } as any)}
              <Space style={{ padding: "2px" }}>
                {formatDate({
                  date: record.transaction_date,
                  format: "time",
                  timeZone: DEFAULT_TIMEZONE,
                } as any)}
              </Space>
            </div>
          )}
        />

        <Column<UserWithithdraw>
          title={"Auth Id"}
          dataIndex={"auth"}
          render={(auth) => auth || "-"}
        />
        <Column<UserWithithdraw>
          title="Order Id"
          width={250}
          dataIndex="ottu_order_no"
          render={(ottu_order_no) => ottu_order_no || "-"}
        />
        {permissions.withdraw.paymentMethodView && (
          <Column<UserWithithdraw>
            title={"Payment Method"}
            dataIndex={"payment_method"}
          />
        )}
        <Column<UserWithithdraw>
          title={t("withdraw.table.Status")}
          dataIndex={"status_name"}
          // width={screens.xxl ? 'auto' : 100}
          width="200px"
          render={(_, record: any) => (
            <>
              <span>{`${record.status_name}`}</span>
            </>
          )}
        />

        {/* <Column<UserWithithdraw>
          title={t("withdraw.table.action")}
          dataIndex={"last_transaction_date"}
          width={200}
          render={(_, record: any) => {
            const isRejectedBySuperAdmin = record.status_id === 5;
            const isApprovedBySuperAdmin =
              record.status_name === "Approved By SuperAdmin";
            const isApprovedByAccountant =
              record.status_name === "Approved By Accountant";
            const isRejectedByAccountant =
              record.status_name === "Rejected By Accountant";
            const canApprove = record.status_id === 1;
            const canReject = record.status_id === 1;
            const hideActionButtons = [6, 7, 8, 9].includes(record.status_id);

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                  minHeight: "40px",
                }}
              >
                {hideActionButtons ? null : (
                  <>
                    {isRejectedBySuperAdmin || isRejectedByAccountant ? (
                      <Button
                        danger
                        size="middle"
                        style={{
                          fontSize: "12px",
                          height: "30px",
                          padding: "0 10px",
                        }}
                        disabled
                      >
                        Rejected
                      </Button>
                    ) : isApprovedBySuperAdmin || isApprovedByAccountant ? (
                      <Button
                        type="primary"
                        size="middle"
                        style={{
                          fontSize: "12px",
                          height: "30px",
                          padding: "0 10px",
                        }}
                        disabled
                      >
                        Approved
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="primary"
                          size="middle"
                          style={{
                            fontSize: "12px",
                            height: "30px",
                            padding: "0 10px",
                          }}
                          disabled={!canApprove}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleValue(record.id);
                          }}
                        >
                          {canApprove ? "Approve" : "Approved"}
                        </Button>

                        <Button
                          danger
                          size="middle"
                          style={{
                            fontSize: "12px",
                            height: "30px",
                            padding: "0 10px",
                          }}
                          disabled={!canReject}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleValueRejectORManual(record.id, "1");
                          }}
                        >
                          {canReject ? "Reject" : "Rejected"}
                        </Button>
                      </>
                    )}
                  </>
                )}

                <Dropdown menu={{ items: items(record) }} trigger={["click"]}>
                  <a onClick={(e) => e.preventDefault()}>
                    <MoreOutlined
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    />
                  </a>
                </Dropdown>
              </div>
            );
          }}
        /> */}
        <Column<UserWithithdraw>
          title={t("withdraw.table.action")}
          dataIndex={"last_transaction_date"}
          width={200}
          render={(_, record: any) => {
            // 1. Check if this specific row is currently selected
            const isRowSelected = selectedRowKeys.includes(record.id);

            const isRejectedBySuperAdmin = record.status_id === 5;
            const isApprovedBySuperAdmin =
              record.status_name === "Approved By SuperAdmin";
            const isApprovedByAccountant =
              record.status_name === "Approved By Accountant";
            const isRejectedByAccountant =
              record.status_name === "Rejected By Accountant";

            const canApprove = record.status_id === 1;
            const canReject = record.status_id === 1;
            const hideActionButtons = [6, 7, 8, 9].includes(record.status_id);

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                  minHeight: "40px",
                }}
              >
                {/* 2. Logic: Hide buttons if status is in hide list OR if the row is selected */}
                {hideActionButtons || isRowSelected ? null : (
                  <>
                    {isRejectedBySuperAdmin || isRejectedByAccountant ? (
                      <Button
                        danger
                        size="middle"
                        style={{
                          fontSize: "12px",
                          height: "30px",
                          padding: "0 10px",
                        }}
                        disabled
                      >
                        Rejected
                      </Button>
                    ) : isApprovedBySuperAdmin || isApprovedByAccountant ? (
                      <Button
                        type="primary"
                        size="middle"
                        style={{
                          fontSize: "12px",
                          height: "30px",
                          padding: "0 10px",
                        }}
                        disabled
                      >
                        Approved
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="primary"
                          size="middle"
                          style={{
                            fontSize: "12px",
                            height: "30px",
                            padding: "0 10px",
                          }}
                          disabled={!canApprove}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleValue(record.id);
                          }}
                        >
                          {canApprove ? "Approve" : "Approved"}
                        </Button>

                        <Button
                          danger
                          size="middle"
                          style={{
                            fontSize: "12px",
                            height: "30px",
                            padding: "0 10px",
                          }}
                          disabled={!canReject}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleValueRejectORManual(record.id, "1");
                          }}
                        >
                          {canReject ? "Reject" : "Rejected"}
                        </Button>
                      </>
                    )}
                  </>
                )}

                <Dropdown menu={{ items: items(record) }} trigger={["click"]}>
                  <a onClick={(e) => e.preventDefault()}>
                    <MoreOutlined
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    />
                  </a>
                </Dropdown>
              </div>
            );
          }}
        />
      </Table>
      <ApproveModal
        open={openApproveModal}
        onCancel={() => setOpenApproveModal(false)}
        onConfirm={(remark: string) => {
          handleBulkActionClick(1, remark); // Approve
          setOpenApproveModal(false);
        }}
      />
      <RejectModal
        open={openRejectModal}
        onCancel={() => setOpenRejectModal(false)}
        onConfirm={(remark: string) => {
          handleBulkActionClick(2, remark); // Approve
          setOpenRejectModal(false);
        }}
      />
    </StyledTableWrapper>
  );
};
